import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { useToasts } from "../../state/toasts";
import { Empty, Modal } from "../../ui/components";
import { dateTime, money } from "../../ui/format";
import { UNIT_TITLES } from "../../ui/units";

/**
 * Закупки: что кончается и заявки поставщику.
 *
 * Отвечает на ежедневный вопрос точки — что взять на завтра. Пока его решают
 * памятью, забывают ровно то, что кончилось вчера, и узнают об этом от кухни
 * в час пик.
 */

interface DemandRow {
  ingredientId: string;
  title: string;
  groupTitle: string | null;
  unit: string;
  onHand: string;
  minStock: string;
  perDay: string;
  daysLeft: number | null;
  suggested: string;
  lastPrice: string | null;
  belowMin: boolean;
}

interface PurchaseLine {
  id: string;
  ingredientId: string;
  title: string;
  unit: string;
  quantity: number;
  entryUnit: string;
  price: number | null;
  amount: number | null;
}

interface PurchaseOrder {
  id: string;
  number: number;
  status: "draft" | "sent" | "received" | "cancelled";
  supplierTitle: string | null;
  expectedAt: string | null;
  comment: string;
  actor: string;
  createdAt: string;
  lines: PurchaseLine[];
  amount: number;
}

const STATUS_TITLES: Record<PurchaseOrder["status"], string> = {
  draft: "Черновик",
  sent: "Отправлена",
  received: "Принята",
  cancelled: "Отменена"
};

/** На сколько дней вперёд закупаемся. Неделя — обычный шаг для фудтрака. */
const COVER_OPTIONS = [3, 7, 14];

export function PurchasesPage() {
  const { run, notify } = useToasts();

  const [coverDays, setCoverDays] = useState(7);
  const [demand, setDemand] = useState<DemandRow[] | null>(null);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [picked, setPicked] = useState<Record<string, number>>({});
  const [receiving, setReceiving] = useState<PurchaseOrder | null>(null);

  const load = useCallback(async () => {
    try {
      const [demandData, orderData] = await Promise.all([
        api.get<{ items: DemandRow[] }>(`/api/admin/stock/demand?days=${coverDays}`),
        api.get<{ items: PurchaseOrder[] }>("/api/admin/stock/purchases")
      ]);

      setDemand(demandData.items);
      setOrders(orderData.items);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось загрузить закупки", "error");
    }
  }, [coverDays, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!demand) return <Empty>Считаем, что кончается…</Empty>;

  const chosen = demand.filter(row => (picked[row.ingredientId] ?? Number(row.suggested)) > 0);

  async function createOrder() {
    const lines = chosen.map(row => ({
      ingredientId: row.ingredientId,
      // Заявка идёт в базовых единицах: расчёт ведётся в них же, и перевод
      // в килограммы туда-обратно только добавил бы округлений.
      quantity: picked[row.ingredientId] ?? Number(row.suggested),
      unit: row.unit,
      ...(row.lastPrice ? { price: Number(row.lastPrice) } : {})
    }));

    const ok = await run(() => api.once.post("/api/admin/stock/purchases", { lines }), "Заявка создана");
    if (ok) {
      setPicked({});
      await load();
    }
  }

  async function setStatus(order: PurchaseOrder, status: "sent" | "cancelled") {
    const ok = await run(
      () => api.patch(`/api/admin/stock/purchases/${order.id}/status`, { status }),
      status === "sent" ? "Заявка отправлена" : "Заявка отменена"
    );
    if (ok) await load();
  }

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Склад</p>
          <h1>Закупки</h1>
        </div>
        {chosen.length > 0 ? (
          <button type="button" className="btn btn-primary" onClick={() => void createOrder()}>
            Собрать заявку · {chosen.length}
          </button>
        ) : null}
      </div>

      <section className="panel">
        <header>
          <div>
            <h2>Что кончается</h2>
            <p>
              Считается от расхода за последние две недели, а не от неснижаемого остатка: минимум
              задан однажды и обычно на глаз, а расход показывает, что происходит на самом деле.
            </p>
          </div>

          <div className="filters">
            {COVER_OPTIONS.map(days => (
              <button
                key={days}
                type="button"
                className={`filter ${coverDays === days ? "active" : ""}`}
                onClick={() => setCoverDays(days)}
              >
                на {days} дн.
              </button>
            ))}
          </div>
        </header>

        {demand.length === 0 ? (
          <Empty>Запаса хватает — заказывать нечего.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Позиция</th>
                  <th className="num">Остаток</th>
                  <th className="num">Расход в день</th>
                  <th className="num">Хватит на</th>
                  <th className="num">Взять</th>
                  <th className="num">Примерно</th>
                </tr>
              </thead>
              <tbody>
                {demand.map(row => {
                  const quantity = picked[row.ingredientId] ?? Number(row.suggested);
                  const unitTitle = UNIT_TITLES[row.unit] ?? row.unit;

                  return (
                    <tr key={row.ingredientId} className={row.belowMin ? "row-alert" : ""}>
                      <td>
                        {row.title}
                        {row.groupTitle ? (
                          <>
                            <br />
                            <small className="muted">{row.groupTitle}</small>
                          </>
                        ) : null}
                      </td>
                      <td className="num">
                        {Number(row.onHand)} {unitTitle}
                        {row.belowMin ? (
                          <>
                            <br />
                            <small className="muted">ниже минимума</small>
                          </>
                        ) : null}
                      </td>
                      <td className="num">
                        {Number(row.perDay)} {unitTitle}
                      </td>
                      <td className="num">
                        {row.daysLeft === null ? (
                          <span className="muted">расхода не было</span>
                        ) : (
                          `${row.daysLeft} дн.`
                        )}
                      </td>
                      <td className="num">
                        <input
                          type="number"
                          min={0}
                          step="0.001"
                          value={quantity}
                          onChange={event =>
                            setPicked(current => ({
                              ...current,
                              [row.ingredientId]: Number(event.target.value)
                            }))
                          }
                        />
                      </td>
                      <td className="num">
                        {row.lastPrice ? money(quantity * Number(row.lastPrice)) : <span className="muted">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel">
        <header>
          <h2>Заявки</h2>
        </header>

        {orders.length === 0 ? (
          <Empty>Заявок пока не было.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Заявка</th>
                  <th>Поставщик</th>
                  <th>Состояние</th>
                  <th className="num">Позиций</th>
                  <th className="num">Примерно</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id}>
                    <td className="num">
                      №{order.number}
                      <br />
                      <small className="muted">{dateTime(order.createdAt)}</small>
                    </td>
                    <td>{order.supplierTitle ?? <span className="muted">не указан</span>}</td>
                    <td>
                      <span className="chip">{STATUS_TITLES[order.status]}</span>
                    </td>
                    <td className="num">{order.lines.length}</td>
                    <td className="num">{order.amount > 0 ? money(order.amount) : "—"}</td>
                    <td>
                      <div className="row-actions">
                        {order.status === "draft" ? (
                          <button type="button" className="btn btn-sm" onClick={() => void setStatus(order, "sent")}>
                            Отправить
                          </button>
                        ) : null}

                        {order.status === "draft" || order.status === "sent" ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() => setReceiving(order)}
                            >
                              Принять
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => void setStatus(order, "cancelled")}
                            >
                              Отменить
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {receiving ? (
        <ReceiveModal
          order={receiving}
          onClose={() => setReceiving(null)}
          onSaved={async () => {
            setReceiving(null);
            await load();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Приём заявки.
 *
 * Количества и цены правятся: приезжает обычно не ровно то, что просили, и
 * накладная должна отражать факт, а не намерение. Заявка остаётся тем, чем
 * была, — записью о том, что собирались заказать.
 */
function ReceiveModal({
  order,
  onClose,
  onSaved
}: {
  order: PurchaseOrder;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { run } = useToasts();

  const [lines, setLines] = useState(
    order.lines.map(line => ({
      ingredientId: line.ingredientId,
      title: line.title,
      quantity: line.quantity,
      unit: line.entryUnit,
      price: line.price ?? 0
    }))
  );
  const [busy, setBusy] = useState(false);

  const total = lines.reduce((sum, line) => sum + line.quantity * line.price, 0);
  const filled = lines.filter(line => line.quantity > 0);

  async function submit() {
    setBusy(true);

    const ok = await run(
      () =>
        api.once.post(`/api/admin/stock/purchases/${order.id}/receive`, {
          lines: filled.map(line => ({
            ingredientId: line.ingredientId,
            quantity: line.quantity,
            unit: line.unit,
            price: line.price
          }))
        }),
      "Товар принят"
    );

    setBusy(false);
    if (ok) await onSaved();
  }

  return (
    <Modal
      title={`Приём по заявке №${order.number}`}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={filled.length === 0 || busy}
            onClick={() => void submit()}
          >
            Принять и оприходовать
          </button>
        </>
      }
    >
      <p className="tiny muted">
        Поправьте количества и цены под накладную. Чего не привезли — поставьте ноль, такая строка
        в приход не попадёт.
      </p>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Позиция</th>
              <th className="num">Количество</th>
              <th>Единица</th>
              <th className="num">Цена</th>
              <th className="num">Сумма</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={line.ingredientId}>
                <td>{line.title}</td>
                <td className="num">
                  <input
                    type="number"
                    min={0}
                    step="0.001"
                    value={line.quantity}
                    onChange={event =>
                      setLines(current =>
                        current.map((row, position) =>
                          position === index ? { ...row, quantity: Number(event.target.value) } : row
                        )
                      )
                    }
                  />
                </td>
                <td>{UNIT_TITLES[line.unit] ?? line.unit}</td>
                <td className="num">
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={line.price}
                    onChange={event =>
                      setLines(current =>
                        current.map((row, position) =>
                          position === index ? { ...row, price: Number(event.target.value) } : row
                        )
                      )
                    }
                  />
                </td>
                <td className="num">{money(line.quantity * line.price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <strong>Итого: {money(total)}</strong>
    </Modal>
  );
}
